import { Flex, Text } from '@chakra-ui/react';
import React from 'react';

import config from 'configs/app';
import type { NetworkSwitcherOption } from 'lib/networkSwitcher';
import { getStoredNetworkChoice, setStoredNetworkChoice } from 'lib/networkSwitcher';
import { IconButton } from 'toolkit/chakra/icon-button';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from 'toolkit/chakra/menu';
import { Tooltip } from 'toolkit/chakra/tooltip';
import NetworkGlyph from 'ui/snippets/networkLogo/NetworkGlyph';

const OPTIONS: Array<{ value: NetworkSwitcherOption; label: string }> = [
  { value: 'mainnet', label: 'Mainnet' },
  { value: 'testnet', label: 'Testnet' },
];

const NetworkSwitcher = () => {
  const feature = config.features.networkSwitcher;
  // NEXT_PUBLIC_IS_TESTNET says which network *this* deployment actually is,
  // so the switcher defaults to matching reality instead of always "Mainnet"
  const defaultOption: NetworkSwitcherOption = config.chain.isTestnet ? 'testnet' : 'mainnet';

  // render the real default on first paint (server + first client render match),
  // then hydrate the stored choice from localStorage right after mount
  const [ currentOption, setCurrentOption ] = React.useState<NetworkSwitcherOption>(defaultOption);

  React.useEffect(() => {
    const stored = getStoredNetworkChoice();

    if (stored) {
      setCurrentOption(stored);
    } else {
      setStoredNetworkChoice(defaultOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const value = event.currentTarget.getAttribute('data-value') as NetworkSwitcherOption | null;

    if (!feature.isEnabled || !value || value === currentOption) {
      return;
    }

    setCurrentOption(value);
    setStoredNetworkChoice(value);

    const url = value === 'testnet' ? feature.testnetUrl : feature.mainnetUrl;
    url && (window.location.href = url);
  }, [ feature, currentOption ]);

  if (!feature.isEnabled) {
    return null;
  }

  const options = OPTIONS.filter((option) => option.value === 'mainnet' || Boolean(feature.testnetUrl));

  return (
    <MenuRoot positioning={{ placement: 'bottom-end' }}>
      <Tooltip content="Switch network">
        <Flex alignItems="center">
          <MenuTrigger asChild>
            <IconButton
              variant="link"
              size="2xs"
              borderRadius="sm"
              aria-label="Switch network"
            >
              <NetworkGlyph/>
            </IconButton>
          </MenuTrigger>
        </Flex>
      </Tooltip>
      <MenuContent minW="150px">
        { options.map((option) => (
          <MenuItem
            key={ option.value }
            value={ option.value }
            data-value={ option.value }
            onClick={ handleSelect }
            cursor="pointer"
            transitionProperty="background-color, color"
            transitionDuration="fast"
          >
            <Text
              as="span"
              fontWeight={ option.value === currentOption ? 600 : 400 }
              color={ option.value === currentOption ? 'link.primary' : 'text.secondary' }
            >
              { option.label }
            </Text>
          </MenuItem>
        )) }
      </MenuContent>
    </MenuRoot>
  );
};

export default React.memo(NetworkSwitcher);
